import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { Col, FormGroup, Row, Spinner } from "reactstrap";
import DataTable from "../../shared/base-components/DataTable";
import SelectBox from "../../shared/base-components/SelectBox";

import paths from "../../shared/route/paths";
import useCookie from "../../shared/hooks/useCookie";

import { useCustomersRetrievalsListMutation } from "./Receivable.actions";
import { partialTypes } from "./Receivable.constants";
import { formatDecimal } from "../../shared/utils/formatText";
import { useRecoilState } from "recoil";
import { filterSelector } from "../../shared/actions/LayoutMode.actions";

const CustomerList = () => {
  document.title =
    "Müşteriler | Bu ekranda müşteri ekleme güncelleme yapabilirsiniz.";
  const isMobile: boolean = /iPhone|iPad|iPod|Android/i.test(
    navigator.userAgent
  );

  const {
    data: customersRetrivealsListData,
    mutate,
    isLoading,
  } = useCustomersRetrievalsListMutation();

  const [filterState, setFilterState] = useRecoilState(filterSelector);

  const [_, setCookieCustomerProfileId] = useCookie("customerProfileId");
  const [selectedPartialType, setSelectedPartialType] = useState<any>(0);

  useEffect(() => {
    mutate({});
  }, []);

  const columns = useMemo(
    () => [
      {
        Header: "Ad Soyad",
        filterable: false,
        disableFilters: true,
        accessor: "fullname",
        Cell: (response: any) => (
          <>
            <h5 className="font-size-14 mb-1">
              <Link
                onClick={() =>
                  setCookieCustomerProfileId(response.row.original.id, 90)
                }
                to={paths.CUSTOMER.CUSTORMER_PROFILE}
                className="text-dark"
              >
                {response.row.original.fullname}
              </Link>
            </h5>
          </>
        ),
      },
      {
        Header: "İl",
        accessor: "country",
        Cell: (response: any) => {
          const data = response.row.original;
          return <p className="text mb-0">{data?.country}</p>;
        },
      },
      {
        Header: "İlçe",
        accessor: "district",
        Cell: (response: any) => {
          const data = response.row.original;
          return <p className="text mb-0">{data?.district}</p>;
        },
      },
      {
        Header: "Mahalle",
        accessor: "neighbourhood",
        Cell: (response: any) => {
          const data = response.row.original;
          return <p className="text mb-0">{data?.neighbourhood}</p>;
        },
      },
      {
        Header: "Cadde/Sokak",
        accessor: "street",
        Cell: (response: any) => {
          const data = response.row.original;
          return <p className="text mb-0">{data?.street}</p>;
        },
      },
      {
        Header: "Kısa Tarif",
        accessor: "address_description",

        Cell: (response: any) => {
          return (
            <p className="text mb-0">
              {response.row.original?.address_description}
            </p>
          );
        },
      },
      {
        Header: "Telefon",
        accessor: "phone_number",
        Cell: (response: any) => {
          return (
            <p className="text mb-0">{response.row.original?.phone_number}</p>
          );
        },
      },
      {
        Header: "Tür",
        accessor: "has_partial",
        filterable: true,
        sortable: true,
        disableFilters: true,
        Cell: (response: any) => {
          const hasPartial = response.row.original?.has_partial;
          const color = hasPartial ? "warning" : "success";
          return (
            <div
              className={
                "badge bg-" + color + "-subtle text-" + color + " font-size-12"
              }
            >
              {hasPartial ? "Taksit" : "Peşin"}
            </div>
          );
        },
      },
      {
        Header: "Sonraki Ödeme Tarihi",
        accessor: "date_of_start",
        filterable: false,
        disableFilters: true,
        Cell: (user: any) => (
          <>
            <p className="text mb-0">
              {user.row.original?.upcoming_payment != null
                ? user.row.original?.upcoming_payment.split("T")[0]
                : "Tarih yok"}
            </p>
          </>
        ),
      },
      {
        Header: "Borç",
        accessor: "retrival",
        filterable: false,
        disableFilters: true,
        Cell: (response: any) => (
          <>
            {
              <p className="text mb-0">
                {formatDecimal(response.row.original?.retrival)} ₺
              </p>
            }
          </>
        ),
      },
    ],
    []
  );

  const mobileColumns = useMemo(
    () => [
      {
        Header: "Ad Soyad/Telefon",
        filterable: false,
        disableFilters: true,
        accessor: "fullname",
        Cell: (response: any) => (
          <>
            <h5 className="font-size-14 mb-1">
              <Link
                onClick={() =>
                  setCookieCustomerProfileId(response.row.original.id, 90)
                }
                to={
                  response.row.original.is_active
                    ? paths.CUSTOMER.CUSTORMER_PROFILE
                    : "#"
                }
                className="text-dark"
              >
                {response.row.original.fullname}

                <p className="font-size-13 text-muted mt-2">
                  {response.row.original.phone_number}
                </p>
              </Link>
            </h5>
          </>
        ),
      },
      {
        Header: "Borç",
        accessor: "retrival",
        filterable: false,
        disableFilters: true,
        Cell: (response: any) => (
          <>
            {
              <p className="text mb-0">
                {formatDecimal(response.row.original?.retrival)} ₺
              </p>
            }
          </>
        ),
      },
    ],
    []
  );

  const partialBool = selectedPartialType === 2;

  const filteredCustomerRetrivealsList =
    customersRetrivealsListData?.results.filter(
      (item) => item?.has_partial === partialBool
    );

  return (
    <React.Fragment>
      <Row className="align-items-center">
        <Col sm={12} md={8} lg={8}>
          <div className="mb-3">
            <h5 className="card-title">
              Alınacaklar Listesi
              <span className="text-muted fw-normal ms-2">
                {isLoading ? (
                  <Spinner className="ms-1" color="primary" />
                ) : (
                  `(${customersRetrivealsListData?.results.length})`
                )}
              </span>
            </h5>
          </div>
        </Col>

        <Col sm={12} md={4} lg={4}>
          <div className="d-flex flex-wrap align-items-center justify-content-end mb-3">
            <SelectBox
              name="devicePart"
              className="form-control"
              onChangeHandler={(e) =>
                setSelectedPartialType(parseInt(e.target.value))
              }
              options={partialTypes}
            />
          </div>
        </Col>
      </Row>

      <Row>
        <Col lg="12">
          {customersRetrivealsListData?.results?.length ? (
            <DataTable
              columns={isMobile ? mobileColumns : columns}
              data={
                selectedPartialType === 0
                  ? customersRetrivealsListData.results
                  : filteredCustomerRetrivealsList
              }
              setFilterState={setFilterState}
              pageIndexList={filterState.pageIndex}
              pageCountTable={
                filteredCustomerRetrivealsList?.length
                  ? filteredCustomerRetrivealsList?.length
                  : customersRetrivealsListData.count
              }
              hasCustomPagination
              isGlobalFilter={true}
              isAddUserList={true}
              customPageSize={10}
              className="table align-middle datatable dt-responsive table-check nowrap"
            />
          ) : (
            !isLoading && <>Herhangi bir alınacak ücret bulunmamaktadır.</>
          )}
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default CustomerList;
